/**
 * Billing Claim API Service
 */

import apiClient from './apiClient';
import {
  BillingClaimDTO,
  CreateBillingClaimRequest,
  ClaimStatus,
  ClaimType,
  PageResponse,
} from '../types/billing.types';

const BASE_PATH = '/v1/billing-claims';

class BillingService {
  async createBillingClaim(request: CreateBillingClaimRequest): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(BASE_PATH, request);
    return response.data;
  }

  async getBillingClaimById(id: string): Promise<BillingClaimDTO> {
    const response = await apiClient.get<BillingClaimDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  }

  async getBillingClaimByClaimNumber(claimNumber: string): Promise<BillingClaimDTO> {
    const response = await apiClient.get<BillingClaimDTO>(`${BASE_PATH}/claim-number/${claimNumber}`);
    return response.data;
  }

  async getAllBillingClaims(page: number = 0, size: number = 20): Promise<PageResponse<BillingClaimDTO>> {
    const response = await apiClient.get<PageResponse<BillingClaimDTO>>(BASE_PATH, {
      params: { page, size },
    });
    return response.data;
  }

  async getBillingClaimsByResident(residentId: string): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/resident/${residentId}`);
    return response.data;
  }

  async getBillingClaimsByStatus(status: ClaimStatus): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/status/${status}`);
    return response.data;
  }

  async getBillingClaimsByType(claimType: ClaimType): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/type/${claimType}`);
    return response.data;
  }

  async updateBillingClaim(id: string, data: Partial<BillingClaimDTO>): Promise<BillingClaimDTO> {
    const response = await apiClient.put<BillingClaimDTO>(`${BASE_PATH}/${id}`, data);
    return response.data;
  }

  async submitBillingClaim(id: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/submit`);
    return response.data;
  }

  async approveBillingClaim(id: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/approve`);
    return response.data;
  }

  async denyBillingClaim(id: string, denialReason: string, denialCode?: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/deny`, null, {
      params: { denialReason, denialCode },
    });
    return response.data;
  }

  async deleteBillingClaim(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }
}

export default new BillingService();
